import { useState } from "react";
import {
  ColorIndicator,
  ColorText,
  ColorWrapper,
  FinishButton,
  FullWrapper,
  InnerColorWrapper,
  MobileSelectorWrapper,
  NotVisibleIndicator,
  SelectorWrapper,
} from "./styled";
import { useDetectClickOutside } from "react-detect-click-outside";
import { ChromePicker, SketchPicker, ColorResult } from "react-color";
import Button from "../button";
import { useMediaQuery } from "react-responsive";
import { MOBILE_VIEW } from "../../variables";
import { getIcon } from "../../utils/get-icon";

interface IColorSelectProps {
  onChange: (color: ColorResult) => void;
  color: string;
  onReset: () => void;
}

// Helper function to check if a color is in rgba format
const isRgba = (color: string): boolean => {
  return color.startsWith("rgba");
};

// Helper to parse rgba string to get alpha
const getRgbaAlpha = (color: string): number => {
  if (!isRgba(color)) return 1;
  const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
  return match ? parseFloat(match[4]) : 1;
};

const ColorSelect: React.FC<IColorSelectProps> = ({
  onChange,
  color,
  onReset,
}) => {
  const isMobile = useMediaQuery({ query: `(max-width: ${MOBILE_VIEW}px)` });
  const [open, setOpen] = useState<boolean>(false);

  const handleClickOutside = () => {
    setOpen(false);
  };

  const ref = useDetectClickOutside({ onTriggered: handleClickOutside });

  const clickSelect = () => {
    setOpen(!open);
  };

  const clickReset = () => {
    setOpen(false);
    onReset();
  };

  // Parse the color for the color picker
  const parseColorForPicker = () => {
    if (!color) return "#ffffff";

    if (isRgba(color)) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        const [, r, g, b, a] = match;
        return {
          r: parseInt(r),
          g: parseInt(g),
          b: parseInt(b),
          a: parseFloat(a),
        };
      }
    }

    return color;
  };

  // For displaying the color in the UI
  const renderColorText = () => {
    if (!color) return "Ingen";

    if (isRgba(color)) {
      const alpha = getRgbaAlpha(color);
      const opacityPercent = Math.round(alpha * 100);
      return `${color} (${opacityPercent}% opaque)`;
    }

    return color.toUpperCase();
  };

  const onClickFinish = () => {
    setOpen(false);
  };

  // Handle color change from the picker
  const handleColorChange = (colorResult: ColorResult) => {
    // If the picker returns RGB values with alpha, ensure we use those
    if (colorResult.rgb) {
      const { r, g, b, a = 1 } = colorResult.rgb;
      onChange({
        ...colorResult,
        hex: colorResult.hex,
        rgb: { r, g, b, a },
      });
    } else {
      onChange(colorResult);
    }
  };

  const renderPicker = () => {
    const pickerColor = parseColorForPicker();

    return isMobile ? (
      <MobileSelectorWrapper>
        <ChromePicker
          color={pickerColor}
          onChange={handleColorChange}
          onChangeComplete={handleColorChange}
          disableAlpha={false} // Make sure alpha is enabled
        />
        <FinishButton onClick={onClickFinish}>{getIcon("done")}</FinishButton>
      </MobileSelectorWrapper>
    ) : (
      <SelectorWrapper>
        <SketchPicker
          color={pickerColor}
          onChange={handleColorChange}
          onChangeComplete={handleColorChange}
          disableAlpha={false} // Make sure alpha is enabled
        />
      </SelectorWrapper>
    );
  };

  return (
    <FullWrapper ref={ref}>
      <ColorWrapper>
        <InnerColorWrapper onClick={clickSelect}>
          {color ? (
            <ColorIndicator
              style={{ backgroundColor: color ?? "transparent" }}
            />
          ) : (
            <NotVisibleIndicator>{getIcon("not-visible")}</NotVisibleIndicator>
          )}
          <ColorText onClick={clickSelect}>{renderColorText()}</ColorText>
          <Button
            onClick={clickReset}
            color="transparent"
            variant="icon"
            icon="exit"
          />
        </InnerColorWrapper>
      </ColorWrapper>
      {open && renderPicker()}
    </FullWrapper>
  );
};

export default ColorSelect;
